import axios from 'axios';
import { getApiBaseUrl, setupAxiosAuth } from '../utils/urlConfig';
import logger from '../utils/logger';

// Get API base URL from centralized configuration
const API_BASE_URL = getApiBaseUrl();

logger.log(`API Base URL configured as: ${API_BASE_URL}`);

// Helper to ensure auth token is included in requests
const setupRequest = () => {
  setupAxiosAuth(axios);
};

/**
 * Fetches details for a specific sync operation
 * (Kept for viewing historical/completed sync details in modal)
 */
export const fetchSyncDetails = async (syncId) => {
  try {
    setupRequest(); // Ensure token is set
    logger.log(`Fetching sync details for ID: ${syncId}`);
    const response = await axios.get(`${API_BASE_URL}/sync/status/${syncId}`);
    
    // Check if the response contains a logs array and determine if we should try to fetch logs separately
    if (!response.data.logs) {
      // Skip trying to fetch logs for content-attempts-sync as the endpoint doesn't exist yet
      const syncType = syncId.split('-')[0]; // Extract the sync type from the ID
      const skipTypes = ['content-attempts']; // Types where we know logs endpoint isn't available
      
      if (!skipTypes.some(type => syncId.includes(type))) {
        // Only try to fetch logs for types that aren't in the skip list
        try {
          const logsResponse = await axios.get(`${API_BASE_URL}/sync/logs/${syncId}`);
          if (logsResponse.data && Array.isArray(logsResponse.data)) {
            response.data.logs = logsResponse.data;
          }
        } catch (logsError) {
          logger.log(`No separate logs endpoint available for sync ${syncId} or error fetching logs:`, logsError);
          // Continue with the original response even if logs fetch fails
        }
      } else {
        logger.log(`Skipping logs fetch for ${syncId} - known endpoint not available`);
      }
    }
    
    return response.data;
  } catch (error) {
    logger.error(`Error fetching sync details for ${syncId}:`, error);
    throw error; // Re-throw other errors
  }
};

/**
 * Fetches course sync details
 */
export const fetchCourseDetails = async () => {
  try {
    setupRequest(); // Ensure token is set
    logger.log('Fetching course sync details');
    const response = await axios.get(`${API_BASE_URL}/sync/courses/details`);
    return response.data;
  } catch (error) {
    logger.error('Error fetching course sync details:', error);
    throw error;
  }
};

/**
 * Triggers a sync operation
 */
export const triggerSync = async (type) => {
  try {
    setupRequest(); // Ensure token is set
    logger.log(`Triggering ${type} sync`);
    const response = await axios.post(`${API_BASE_URL}/sync/trigger`, { type });
    return response.data;
  } catch (error) {
    logger.error(`Error triggering ${type} sync:`, error);
    throw error;
  }
};

/**
 * Triggers a fast content sync for a specific course
 * Uses the optimized endpoint that skips unchanged content
 */
export const triggerFastContentSync = async (courseId) => {
  try {
    setupRequest();
    logger.log(`Triggering fast content sync for course ${courseId}`);
    const response = await axios.get(`${API_BASE_URL}/sync/chapter-content-fast/${courseId}`);
    return response.data;
  } catch (error) {
    logger.error(`Error triggering fast content sync for course ${courseId}:`, error);
    throw error;
  }
};

/**
 * Resets stuck sync operations
 */
export const resetStuckSyncs = async () => {
  try {
    setupRequest(); // Ensure token is set
    logger.log('Resetting stuck syncs');
    const response = await axios.get(`${API_BASE_URL}/sync/reset`);
    return response.data;
  } catch (error) {
    logger.error('Error resetting stuck syncs:', error);
    throw error;
  }
};

/**
 * Triggers date-limited syncs for content attempts and exam attempts.
 * Content attempts are synced for yesterday.
 * Exam attempts are synced from yesterday to today.
 */
export const triggerYesterdayToTodayAttemptsSync = async () => {
  try {
    setupRequest();
    logger.log('Triggering async yesterday-to-today attempts sync');
    const response = await axios.get(`${API_BASE_URL}/sync/attempts-yesterday-today`);
    return response.data;
  } catch (error) {
    logger.error('Error triggering yesterday-to-today attempts sync:', error);
    throw error;
  }
};

/**
 * Triggers a background sync of all attempts (+ answers) for ONE non-practice exam.
 * Fetches fresh from the Testpress API. Optional { date_0, date_1 } (yyyy-mm-dd)
 * narrows the attempts by date range.
 * Returns { success, syncId, examId }.
 */
export const triggerExamAttemptsSync = async (examId, { date_0, date_1 } = {}) => {
  try {
    setupRequest();
    const params = {};
    if (date_0) params.date_0 = date_0;
    if (date_1) params.date_1 = date_1;
    logger.log(`Triggering exam attempts sync for exam ${examId}`, params);
    const response = await axios.get(`${API_BASE_URL}/sync/exam-attempts/${examId}`, { params });
    return response.data;
  } catch (error) {
    logger.error(`Error triggering exam attempts sync for exam ${examId}:`, error);
    throw error;
  }
};

/**
 * Fetches the latest sync details for a specific sync TYPE
 */
export const getLatestSyncDetails = async (syncType) => {
  let endpoint = '';
  switch (syncType) {
    case 'users':
    case 'users-sync':
      endpoint = `${API_BASE_URL}/sync/users/details`;
      break;
    case 'courses':
      endpoint = `${API_BASE_URL}/sync/courses/details`;
      break;
    case 'chapters': // Assuming a /sync/chapters/details endpoint exists or will be added
      endpoint = `${API_BASE_URL}/sync/chapters/details`; 
      break;
    case 'content': // Assuming content uses /sync/content/details
    case 'content-sync':
      endpoint = `${API_BASE_URL}/sync/content/details`;
      break;
    case 'attempt-answers':
    case 'attempt-answers-sync':
      endpoint = `${API_BASE_URL}/sync/attempt-answers/details`; // Assuming this endpoint exists
      break;
    case 'single-attempts':
    case 'single-attempts-sync':
      endpoint = `${API_BASE_URL}/sync/attempt/details`; // Assuming this endpoint exists
      break;
    case 'batches':
    case 'batch-sync':
      endpoint = `${API_BASE_URL}/sync/batches/details`;
      break;
    // Add cases for other sync types if they have details endpoints
    // case 'content_attempts':
    // case 'content-attempts-sync':
    //   endpoint = `${API_BASE_URL}/sync/content-attempts/details`; // Assuming this endpoint exists
    //   break;
    default:
      logger.warn(`No specific details endpoint configured for sync type: ${syncType}. Trying generic status.`);
      // Fallback or throw error? For now, let's log and potentially let it fail if no generic endpoint exists
      // Or maybe fetch general history? throw new Error(`Unsupported sync type for details: ${syncType}`);
      return { lastSync: null, syncHistory: [] }; // Return empty if no endpoint
  }

  try {
    setupRequest(); // Ensure token is set
    logger.log(`Fetching latest ${syncType} sync details from ${endpoint}`);
    const response = await axios.get(endpoint);
    
    // Special handling for content-sync
    if ((syncType === 'content-sync' || syncType === 'content') && !response.data.contentStats) {
      logger.warn(`Content sync response doesn't contain contentStats:`, response.data);
      
      // If contentStats is missing but we have results in lastSync, create contentStats from it
      if (response.data.lastSync?.results) {
        const lastSync = response.data.lastSync;
        response.data.contentStats = {
          chaptersProcessed: lastSync.results.chaptersProcessed || 0,
          created: lastSync.results.contents?.created || 0,
          updated: lastSync.results.contents?.updated || 0,
          failed: lastSync.results.contents?.failed || 0,
          notFound: lastSync.results.contents?.not_found || 0,
          totalFromApi: lastSync.results.contents?.totalFromApi || 0,
          errors: lastSync.results.errors || []
        };
      } else {
        // Create empty contentStats if nothing is available
        response.data.contentStats = {};
      }
    }
    
    return response.data;
  } catch (error) {
    logger.error(`Error fetching latest sync details for type ${syncType} from ${endpoint}:`, error);
    
    // Create a more detailed error object
    const enhancedError = new Error(`Failed to fetch ${syncType} details: ${error.message}`);
    enhancedError.response = error.response;
    enhancedError.originalError = error;
    
    throw enhancedError; // Re-throw error to be caught by the calling component
  }
};

/**
 * Deletes a specific sync record by ID
 */
export const deleteSyncRecord = async (syncId) => {
  try {
    setupRequest(); // Ensure token is set
    logger.log(`Deleting sync record: ${syncId}`);
    const response = await axios.delete(`${API_BASE_URL}/sync/history/${syncId}`);
    return response.data;
  } catch (error) {
    logger.error(`Error deleting sync record ${syncId}:`, error);
    throw error;
  }
};

/**
 * Deletes all sync history records with optional filters
 */
export const deleteAllSyncHistory = async (filters = {}) => {
  try {
    setupRequest(); // Ensure token is set
    logger.log('Deleting all sync history with filters:', filters);
    const response = await axios.delete(`${API_BASE_URL}/sync/history`, { data: filters });
    return response.data;
  } catch (error) {
    logger.error('Error deleting sync history:', error);
    throw error;
  }
};